import React, { useEffect, useState, memo, useCallback } from 'react';
import { Modal } from '@utils/RNModal'
import { View, Pressable, Platform, StyleSheet, TouchableOpacity, ScrollView, Text } from 'react-native';
import { FontAwesome5, Ionicons, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { useWindowDimensions } from 'react-native';
import styles from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import searchStyles from '../searchStyles';
import { GenerateArrayTypes } from '../Types';
import { Popover } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

const AnimatedIcon = ({ name }) => {
  const theme = themeSelector();
  const { t } = useTranslation();

  return (
    <Animatable.View 
      useNativeDriver 
      animation="fadeInDown" 
      duration={250} 
      style={modernStyles.animatedIconContainer}
    >
      <FontAwesome5 
        name={name} 
        size={name === 'ellipsis-h' ? 20 : 18} 
        color={theme ? "rgba(100, 116, 139, 0.8)" : "rgba(148, 163, 184, 0.8)"} 
        style={modernStyles.animatedIcon}
      />
    </Animatable.View>
  );
};

const SHADOW_BLEED = 8;
/** Button size for the dense web search row (must stay inside the row height). */
export const WEB_DENSE_ICON_SIZE = 28;
export const WEB_DENSE_ICON_GAP = 6;
export const WEB_DENSE_ICON_RADIUS = 8;

const statusBarPillStyle = (theme: boolean) => ({
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  alignSelf: 'flex-start' as const,
  borderRadius: 10,
  shadowRadius: 10,
  shadowOpacity: 0.2,
  backgroundColor: theme ? 'white' : 'black',
  padding: 5,
  shadowColor: 'black',
  shadowOffset: { width: 0, height: 2 },
  overflow: 'visible' as const,
  ...Platform.select({
    android: { elevation: 6 },
    default: {},
  }),
});

const IconItem = ({ item, index, handleFunction, setExtendedMenu, extendedMenu, showLabel = false, compact = false, inlineLabel = false }) => {
  const theme = themeSelector();
  const isOutline = item.variant === 'outline';
  // Dense web search row: buttons must fit inside the row height.
  const webDense = Platform.OS === 'web' && compact && !showLabel && !inlineLabel;
  // Adjust sizes based on whether label is inline, below, or none.
  // If inlineLabel is true, we need a wider button
  const btnSize = webDense ? WEB_DENSE_ICON_SIZE : (inlineLabel ? 36 : (compact ? 36 : (showLabel && !inlineLabel ? 64 : 48)));
  // Dense web: the tinted tile fills the button so both share one shape.
  const innerSize = webDense ? btnSize : (inlineLabel ? 24 : (compact ? 22 : (showLabel && !inlineLabel ? 48 : 32)));
  const glyphSize = webDense ? Math.max(11, btnSize - 14) : Math.max(11, innerSize - 6);
  const renderedIcon = webDense && React.isValidElement(item.icon)
    ? React.cloneElement(item.icon as any, {
        size: Math.min((item.icon as any).props?.size ?? glyphSize, glyphSize),
      })
    : item.icon;
  
  const formattedLabel = item.functionName ? item.functionName.replace(/([A-Z])/g, ' $1').trim() : '';
  const displayLabel = isOutline || /\s/.test(item.functionName || '')
    ? (item.functionName || '')
    : (formattedLabel ? formattedLabel.charAt(0).toUpperCase() + formattedLabel.slice(1) : '');
  const labelColor = isOutline
    ? (theme ? '#1a1a1a' : '#ffffff')
    : (theme ? '#374151' : '#d1d5db');

  if (isOutline) {
    return (
      <View style={{
        alignItems: 'center',
        maxWidth: inlineLabel ? undefined : (showLabel ? 80 : (compact ? 44 : 60)),
        marginBottom: (showLabel && !inlineLabel) ? 8 : 0,
        overflow: 'visible',
      }}>
        <Pressable
          key={item.functionName}
          onPress={() => {
            handleFunction(item.function());
            if (item.extendedOptions && extendedMenu !== item.functionName) {
              setExtendedMenu(item.functionName);
            }
          }}
          style={({ pressed }) => ({
            transform: [{ scale: pressed ? 0.98 : 1 }],
            overflow: 'visible',
          })}
          accessibilityRole="button"
          accessibilityLabel={item.functionName || 'Action'}
        >
          <Animatable.View style={{ padding: 2, overflow: 'visible' }}>
            <View
              style={[
                statusBarPillStyle(theme),
                {
                  flexDirection: inlineLabel ? 'row' : 'column',
                },
              ]}
            >
              <View style={{ marginRight: inlineLabel && item.functionName ? 6 : 0 }}>
                {item.icon}
              </View>
              {inlineLabel && item.functionName ? (
                <Text
                  style={{
                    fontSize: responsiveFontSize(13),
                    color: labelColor,
                    fontWeight: '600',
                  }}
                  numberOfLines={1}
                >
                  {displayLabel}
                </Text>
              ) : null}
            </View>
          </Animatable.View>
        </Pressable>
        {showLabel && !inlineLabel && item.functionName ? (
          <Text
            style={{
              marginTop: 6,
              fontSize: responsiveFontSize(11),
              color: labelColor,
              textAlign: 'center',
              maxWidth: 70,
            }}
            numberOfLines={2}
          >
            {displayLabel}
          </Text>
        ) : null}
      </View>
    );
  }

  return (
    <View style={{
      alignItems: 'center',
      maxWidth: inlineLabel ? undefined : (webDense ? btnSize : (showLabel ? 80 : (compact ? 44 : 60))),
      marginBottom: (showLabel && !inlineLabel) ? 8 : 0,
      ...(webDense && { height: btnSize, justifyContent: 'center' as const }),
    }}>
      <Pressable
        style={({ pressed }) => [
          modernStyles.iconButton,
          {
            backgroundColor: theme ? "#ffffffff" : "#1e293b",
            borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
            shadowColor: theme ? "#000" : "#fff",
            shadowOpacity: theme ? 0.08 : 0.15,
            transform: [{ scale: pressed ? 0.95 : 1 }],
            width: inlineLabel ? undefined : btnSize,
            height: inlineLabel ? undefined : btnSize,
            // Compact rows must be allowed to shrink below the default 48dp minimum.
            minWidth: inlineLabel ? undefined : Math.min(48, btnSize),
            minHeight: inlineLabel ? undefined : Math.min(48, btnSize),
            ...(webDense && { borderRadius: WEB_DENSE_ICON_RADIUS, shadowOpacity: 0, elevation: 0 }),
            padding: 0,
            overflow: 'hidden',
          },
        ]}
        key={item.functionName}
        onPress={() => {
          handleFunction(item.function());
          if (item.extendedOptions && extendedMenu !== item.functionName) {
            setExtendedMenu(item.functionName);
          }
        }}
        accessibilityRole="button"
        accessibilityLabel={item.functionName || 'Action'}
      >
        {({ hovered }) => (
          <Animatable.View 
            useNativeDriver 
            transition={'backgroundColor'} 
            style={[
              modernStyles.iconButtonContent,
              { 
                backgroundColor: item.isActive ? (theme ? "#4F46E5" : "#6366F1") : (theme ? "#4A90E2" : "#374151"),
                flexDirection: inlineLabel ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: inlineLabel ? 'flex-start' : 'center',
                paddingVertical: webDense ? 0 : (inlineLabel ? 6 : 8),
                paddingHorizontal: webDense ? 0 : 8,
                width: inlineLabel ? undefined : '100%',
                height: inlineLabel ? undefined : '100%',
                ...(webDense && { borderRadius: WEB_DENSE_ICON_RADIUS }),
              },
            ]}
          >
            <View style={[modernStyles.iconWrapper, {
              backgroundColor: item.isActive ? (theme ? "#4F46E5" : "#6366F1") : (theme ? "#4A90E2" : "#374151"),
              width: webDense ? '100%' : innerSize,
              height: webDense ? '100%' : innerSize,
              borderRadius: webDense ? WEB_DENSE_ICON_RADIUS : 0,
              marginRight: inlineLabel && item.functionName ? 6 : 0,
              overflow: 'hidden',
            }]}>
              {renderedIcon}
            </View>
            {inlineLabel && item.functionName && (
              <Text
                style={{
                  fontSize: responsiveFontSize(13),
                  color: labelColor,
                  fontWeight: '600',
                }}
                numberOfLines={1}
              >
                {displayLabel}
              </Text>
            )}
          </Animatable.View>
        )}
      </Pressable>
      {showLabel && !inlineLabel && item.functionName && (
        <Text
          style={{
            marginTop: 6,
            fontSize: responsiveFontSize(11),
            color: labelColor,
            textAlign: 'center',
            maxWidth: 70,
          }}
          numberOfLines={2}
        >
          {displayLabel}
        </Text>
      )}
    </View>
  );
};

const GenerateArray = ({
  customIcons,
  setExtendedMenu,
  extendedMenu,
  disableBurger,
  setShowExtendedMenu,
  customIconStyle,
  width,
  extraComponents,
  otherComponents,
  compact = false,
  modalExtraContent,
  scrollableWithLabels = false,
}: GenerateArrayTypes) => {
  // Calculate actual icon size for breakpoint calculation
  const webDenseRow = Platform.OS === 'web' && compact;
  const iconSize = webDenseRow ? WEB_DENSE_ICON_SIZE : (compact ? 36 : 48);
  const iconGap = webDenseRow ? WEB_DENSE_ICON_GAP : (compact ? 8 : 12);
  const iconCount = customIcons?.length || 0;
  const sizeOfArray = iconCount > 0 ? iconCount * (iconSize + iconGap) + iconGap : 0; // Actual size needed
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const dimensions = useWindowDimensions();
  const [openMenu, setOpenMenu] = useState(false);

  // Memoize breakpoint calculation
  const breakpointAchieved = React.useMemo(() => {
    // If width is provided, check if icons exceed that width
    // If no width, check if icons exceed 50% of screen width
    return width ? sizeOfArray > width : sizeOfArray > (dimensions.width * 0.5);
  }, [width, sizeOfArray, dimensions.width]);

  const handleFunction = useCallback((passedFunction) => {
    setOpenMenu(false);
    if (typeof passedFunction === 'function') {
      passedFunction();
    }
  }, []);

  // Memoize icon rendering function
  const renderIcons = useCallback((showLabel = false, inlineLabel = false) => {
    if (!customIcons || customIcons.length === 0) return null;
    return customIcons.map((item, index) => (
      <IconItem
        key={item.functionName}
        item={item}
        index={index}
        handleFunction={handleFunction}
        setExtendedMenu={setExtendedMenu}
        extendedMenu={extendedMenu}
        showLabel={showLabel}
        compact={compact}
        inlineLabel={inlineLabel}
      />
    ));
  }, [customIcons, handleFunction, extendedMenu, compact]);

  // Memoize other components rendering
  const renderOtherComponentsInModal = useCallback((showLabel = false, inlineLabel = false) => {
    if (!otherComponents || otherComponents.length === 0) return null;

    return otherComponents.map((item, index) => (
      <IconItem
        key={`other-${item.functionName}-${index}`}
        item={item}
        index={index}
        handleFunction={handleFunction}
        setExtendedMenu={setExtendedMenu}
        extendedMenu={extendedMenu}
        showLabel={showLabel}
        compact={compact}
        inlineLabel={inlineLabel}
      />
    ));
  }, [otherComponents, handleFunction, extendedMenu, compact]);

  // On small screens (native mobile), always use burger modal - combine customIcons, otherComponents, extraComponents
  const hasContent = (customIcons?.length ?? 0) > 0 || (otherComponents?.length ?? 0) > 0 || !!extraComponents;
  const showBurger = !isTablet && Platform.OS !== 'web' && !disableBurger && hasContent;

  // Calculate actual icon container width
  // Use the sizeOfArray we already calculated above
  const calculatedWidth = sizeOfArray;
  
  // Use provided width if available, but ensure it's at least the calculated width
  // If provided width is smaller than needed, breakpointAchieved will trigger burger menu
  // Otherwise, use the calculated width to ensure all icons fit
  const containerWidth = width ? Math.max(width, calculatedWidth) : calculatedWidth;

  // Memoize container style
  const containerStyle = React.useMemo(() => [
    modernStyles.iconsContainer,
    showBurger && Platform.OS !== 'web' ? {
      // On mobile with burger, make container narrow and align to end
      width: 60,
      maxWidth: 60,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      marginRight: 0, // Remove default marginRight
      paddingRight: 8,
    } : (!showBurger && !scrollableWithLabels) ? {
    // Normal icon array - use calculated or provided width
      width: Platform.OS === 'web' ? 'auto' : containerWidth,
      maxWidth: Platform.OS === 'web' ? undefined : containerWidth,
      minWidth: Platform.OS === 'web' ? 'auto' : containerWidth,
      marginRight: 0, // Remove default margin to prevent overlap
    } : {
      // scrollableWithLabels
      flexDirection: 'row',
      gap: 16,
      alignItems: 'center',
    },
    webDenseRow && !scrollableWithLabels && {
      gap: WEB_DENSE_ICON_GAP,
      height: WEB_DENSE_ICON_SIZE,
      alignItems: 'center' as const,
    },
    customIconStyle as any
  ], [showBurger, isTablet, customIcons?.length || 0, customIconStyle, containerWidth, width, scrollableWithLabels, webDenseRow]);

  const scrollableLabelPaddingStyle = {
    paddingHorizontal: SHADOW_BLEED,
    paddingVertical: SHADOW_BLEED,
    alignItems: 'center' as const,
  };

  return (
    <View style={[
      searchStyles.iconArrayStyle,
      modernStyles.arrayContainer,
      showBurger && { alignItems: 'flex-end' },
      scrollableWithLabels && { flex: 1, alignItems: 'flex-start', width: '100%', overflow: 'visible' as const },
      webDenseRow && !scrollableWithLabels && {
        gap: WEB_DENSE_ICON_GAP,
        alignItems: 'center' as const,
        height: WEB_DENSE_ICON_SIZE,
      },
    ]}>
      {scrollableWithLabels ? (
        (customIcons?.length ?? 0) <= 1 ? (
          <View style={[containerStyle, scrollableLabelPaddingStyle]}>
            {renderIcons(true, true)}
          </View>
        ) : (
        <ScrollView 
          horizontal
          showsHorizontalScrollIndicator={false}
          clipToPadding={false}
          style={{ width: '100%', flex: 1, overflow: 'visible' }}
          contentContainerStyle={[
            containerStyle,
            scrollableLabelPaddingStyle,
          ]}
        >
          {renderIcons(true, true)}
        </ScrollView>
        )
      ) : (
        <View style={containerStyle}>
          {showBurger ? (
            <BurgerComponent
              customIcons={customIcons}
              renderIcons={renderIcons}
              renderOtherComponents={renderOtherComponentsInModal}
              extraComponents={extraComponents}
              otherComponents={otherComponents}
              openMenu={openMenu}
              setOpenMenu={setOpenMenu}
              modalExtraContent={modalExtraContent}
            />
          ) : (
              renderIcons()
          )}
        </View>
      )}
    </View>
  );
};

const BurgerComponent = ({ customIcons, renderIcons, renderOtherComponents, extraComponents, otherComponents, openMenu, setOpenMenu, modalExtraContent }) => {
  const theme = themeSelector();
  const modalScrollMaxHeight = modalExtraContent ? 420 : 200;
  
  // Use Modal for native, Popover for web
  if (Platform.OS === 'web') {
    return (
      <View style={[
        styles.centerEverything,
        modernStyles.burgerContainer
      ]}>
        <Popover
          stayInFrame
          placement='bottom'
          open={openMenu}
          onOpenChange={setOpenMenu}
        >
          <Popover.Trigger asChild>
            <Animatable.View
              animation="pulse"
              iterationCount="infinite"
              iterationDelay={2000}
              duration={1500}
            >
              <Pressable
                hitSlop={15}
                onPress={() => setOpenMenu(!openMenu)}
                style={({ pressed }) => [
                  modernStyles.burgerButton,
                  {
                    backgroundColor: theme ? "#ffffffff" : "#1e293b",
                    borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
                    shadowColor: theme ? "#000" : "#fff",
                    shadowOpacity: theme ? 0.1 : 0.2,
                    transform: [{ scale: pressed ? 0.9 : 1 }],
                  }
                ]}
                accessibilityRole="button"
                accessibilityLabel="More options"
                accessibilityState={{ expanded: openMenu }}
              >
                <View style={[modernStyles.burgerIconContainer, {
                  backgroundColor: theme ? "#f8fafc" : "#334155"
                }]}>
                  <MaterialIcons
                    name="more-vert"
                    size={18}
                    color={theme ? "#6366f1" : "#8b5cf6"}
                  />
                </View>
              </Pressable>
            </Animatable.View>
          </Popover.Trigger>
          <Popover.Content
            enterStyle={{ y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ y: -10, opacity: 0, scale: 0.95 }}
            animation={[
              'superBouncy',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
          >
            <Popover.Arrow
              borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"}
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
            />
            <View style={[modernStyles.popoverContent, {
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)"
            }]}>
              {renderOtherComponents && renderOtherComponents(true)}
              {renderIcons(true)}
              {extraComponents && (
                <View style={{ marginTop: 8, width: '100%' }}>
                  {extraComponents}
                </View>
              )}
              {modalExtraContent}
            </View>
          </Popover.Content>
        </Popover>
      </View>
    );
  }

  // Native implementation with Modal
  return (
    <View style={[
      styles.centerEverything,
      modernStyles.burgerContainer
    ]}>
      <Animatable.View
        animation="pulse"
        iterationCount="infinite"
        iterationDelay={2000}
        duration={1500}
      >
        <Pressable
          hitSlop={15}
          onPress={() => setOpenMenu(true)}
          style={({ pressed }) => [
            modernStyles.burgerButton,
            {
              backgroundColor: theme ? "#ffffffff" : "#1e293b",
              borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
              shadowColor: theme ? "#000" : "#fff",
              shadowOpacity: theme ? 0.1 : 0.2,
              transform: [{ scale: pressed ? 0.9 : 1 }],
            }
          ]}
          accessibilityRole="button"
          accessibilityLabel="More options"
          accessibilityState={{ expanded: openMenu }}
        >
          <View style={[modernStyles.burgerIconContainer, {
            backgroundColor: theme ? "#f8fafc" : "#334155"
          }]}>
            <MaterialIcons
              name="more-vert"
              size={18}
              color={theme ? "#6366f1" : "#8b5cf6"}
            />
          </View>
        </Pressable>
      </Animatable.View>

      <Modal
        visible={openMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setOpenMenu(false)}
      >
        <TouchableOpacity
          style={modernStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setOpenMenu(false)}
        >
          <Animatable.View
            animation="zoomIn"
            duration={300}
            useNativeDriver
            style={[
              modernStyles.modalContent,
              {
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)",
                maxHeight: modalExtraContent ? '72%' : '50%',
              }
            ]}
          >
            <View style={modernStyles.modalHeader}>
              <View style={modernStyles.modalHandle} />
            </View>
            <ScrollView
              style={[modernStyles.modalScrollView, { maxHeight: modalScrollMaxHeight }]}
              contentContainerStyle={[
                modernStyles.modalIconsContainer,
                modalExtraContent ? { paddingBottom: 24 } : null,
              ]}
              showsVerticalScrollIndicator={false}
            >

              {renderOtherComponents && renderOtherComponents(true)}
              {renderIcons(true)}
              {extraComponents && (
                <View style={{ width: '100%', marginTop: 12, padding: 12 }}>
                  {extraComponents}
                </View>
              )}
              {modalExtraContent ? (
                <View style={{ width: '100%' }}>{modalExtraContent}</View>
              ) : null}

              {/* Fallback content if nothing renders */}
              {!customIcons?.length && !extraComponents && !otherComponents?.length && !modalExtraContent && (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <MaterialIcons name="info-outline" size={48} color={theme ? '#999' : '#666'} />
                  <Text style={{ marginTop: 8, color: theme ? '#666' : '#999', fontSize: responsiveFontSize(14) }}>
                    {t('No actions available')}
                  </Text>
                </View>
              )}
            </ScrollView>
            <Pressable
              style={[modernStyles.closeButton, {
                backgroundColor: theme ? "#f3f4f6" : "#374151"
              }]}
              onPress={() => setOpenMenu(false)}
              accessibilityRole="button"
              accessibilityLabel="Close menu"
            >
              <MaterialIcons
                name="close"
                size={24}
                color={theme ? "#1f2937" : "#f9fafb"}
              />
            </Pressable>
          </Animatable.View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const modernStyles = StyleSheet.create({
  // Container Styles
  arrayContainer: {
    flexGrow: 1,
    alignSelf: "center",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
    overflow: 'visible',
  },

  iconsContainer: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap', // Prevent wrapping
    overflow: 'visible', // Allow icons to be visible
  },

  // Animated Icon Styles
  animatedIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },

  animatedIcon: {
    textAlign: 'center',
  },

  // Icon Button Styles
  iconButton: {
    minWidth: 48,
    minHeight: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 1,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(10px)',
      }
    })
  },

  iconButtonContent: {
    borderRadius: 14,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },

  iconWrapper: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Burger Component Styles
  burgerContainer: {
    maxWidth: 40,
    minWidth: 40,
    alignContent: 'center',
    alignItems: 'flex-end',
    justifyContent: 'center',
    alignSelf: 'flex-end',
  },

  burgerButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2,
    borderWidth: 1,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(8px)',
      }
    })
  },

  burgerIconContainer: {
    width: 24,
    height: 24,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },

  popoverContent: {
    gap: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    borderRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 12,
    borderWidth: 1,
    minWidth: 200,
    maxWidth: 320,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      }
    })
  },

  // Native Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    zIndex: 9999,
  },
  modalContent: {
    borderRadius: 24,
    borderWidth: 1,
    width: '90%',
    maxHeight: '50%',
    minHeight: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 16,
    zIndex: 10000,
  },
  modalHeader: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  modalHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(128, 128, 128, 0.3)',
  },
  modalScrollView: {
    flex: 1,
    maxHeight: 200,
  },
  modalIconsContainer: {
    padding: 16,
    gap: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  closeButton: {
    margin: 8,
    padding: 12,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default memo(GenerateArray);
